/**
 * Shared pagination control for server-paginated list pages.
 *
 * Renders the "Showing X to Y of Z" line, an items-per-page selector,
 * and First/Prev/numbered/Next/Last buttons. The host page owns the
 * `page` and `limit` state and re-fetches when either changes; this
 * component is pure presentation.
 *
 * Styling re-uses the existing .pagination-wrapper / .pagination-info /
 * .pagination / .pagination-btn / .page-btn classes already declared
 * in the global stylesheets (devices.css ships them).
 */

import React from 'react';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total page count from the server response. */
  totalPages: number;
  /** Total item count from the server response. */
  totalItems: number;
  /** Current items-per-page value. */
  pageSize: number;
  /** Allowed values in the items-per-page selector. */
  pageSizeOptions?: number[];
  /** Label of the entity being paginated ("templates", "devices"). */
  itemLabel?: string;
  /** Switch to a new 1-based page. */
  onPageChange: (page: number) => void;
  /** Switch to a new items-per-page (typically resets `page` to 1). */
  onPageSizeChange: (pageSize: number) => void;
  /** Hide everything when the server has zero items. Default false. */
  hideWhenEmpty?: boolean;
}

const DEFAULT_OPTIONS = [10, 25, 50, 100];

export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  totalItems,
  pageSize,
  pageSizeOptions = DEFAULT_OPTIONS,
  itemLabel = 'items',
  onPageChange,
  onPageSizeChange,
  hideWhenEmpty = false,
}) => {
  if (hideWhenEmpty && totalItems === 0) return null;

  const firstShown = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastShown = Math.min(page * pageSize, totalItems);

  // Sliding window of up to 5 numbered page buttons, matching the
  // existing DevicesTable visual rhythm.
  const pages: number[] = [];
  const maxVisible = 5;
  let start = Math.max(1, page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="pagination-wrapper">
      <div className="pagination-info">
        <span>
          Showing {firstShown} to {lastShown} of {totalItems} {itemLabel}
        </span>
        <div className="items-per-page">
          <label htmlFor="paginationPageSize">Items per page:</label>
          <select
            id="paginationPageSize"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="items-per-page-select"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            className="pagination-btn"
            aria-label="First page"
          >
            First
          </button>
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="pagination-btn"
            aria-label="Previous page"
          >
            Previous
          </button>

          <div className="page-numbers">
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`page-btn ${p === page ? 'active' : ''}`}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </button>
            ))}
          </div>

          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="pagination-btn"
            aria-label="Next page"
          >
            Next
          </button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={page >= totalPages}
            className="pagination-btn"
            aria-label="Last page"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
